import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { StealthRobot } from "./StealthRobot";

const isTemp = (id) => /^(tmp-|u_)/.test(String(id || ""));
const GREET = /\b(hi|hello|hey|robot)\b/i;

function Bubble({ state }) {
  const content = state === "surprised" ? "!" : state === "greeting" ? "Hi!"
    : state === "thinking" ? [0, 1, 2].map((i) => <span key={i} className="cmp-dot" />)
    : state === "listening" ? [0, 1, 2, 3].map((i) => <span key={i} className="cmp-bar" style={{ animationDelay: `${i * 0.12}s` }} />)
    : null;
  if (!content) return null;
  return <div key={state} className="companion-bubble" data-testid="chat-robot-bubble">{content}</div>;
}

// Walks left ↔ right above the composer; stops and reacts when the user types, talks or taps it.
export function ChatCompanion({ messages, thinking, listening, speaking, typing }) {
  const [reaction, setReaction] = useState(null);
  const timers = useRef([]);
  const seenUser = useRef(null);
  const seenReply = useRef(null);
  const wasSpeaking = useRef(false);

  const play = (seq) => {
    timers.current.forEach(clearTimeout);
    let t = seq[0][1];
    setReaction(seq[0][0]);
    timers.current = seq.slice(1).map(([s, d]) => { const id = setTimeout(() => setReaction(s), t); t += d; return id; });
    timers.current.push(setTimeout(() => setReaction(null), t));
  };
  useLayoutEffect(() => () => { timers.current.forEach(clearTimeout); seenUser.current = null; seenReply.current = null; }, []);

  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const lastReply = [...messages].reverse().find((m) => m.role !== "user");

  useLayoutEffect(() => {
    if (!lastUser || !isTemp(lastUser.id) || seenUser.current === lastUser.id) return;
    seenUser.current = lastUser.id;
    play(GREET.test(lastUser.text || "") ? [["greeting", 1700]] : [["surprised", 900]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUser?.id]);

  useLayoutEffect(() => {
    if (!lastReply || !lastReply.fresh || seenReply.current === lastReply.id) return;
    seenReply.current = lastReply.id;
    play([["speaking", 2600], ["happy", 1500]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastReply?.id]);

  useEffect(() => {
    if (speaking) { timers.current.forEach(clearTimeout); setReaction(null); }
    else if (wasSpeaking.current) play([["happy", 1400]]);
    wasSpeaking.current = speaking;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speaking]);

  const base = listening ? "listening" : thinking ? "thinking" : speaking ? "speaking" : typing ? "attentive" : "walking";
  const state = listening || speaking ? base : (reaction || base);
  const walking = state === "walking";

  return (
    <div className="companion-lane fade-in-up" data-testid="chat-companion-lane">
      <div className={`companion-walker ${walking ? "" : "is-paused"}`} data-testid="chat-companion" data-state={state} data-walking={walking}>
        <div className="companion-face">
          <button type="button" className="companion-hit" onClick={() => play([["greeting", 1600], ["happy", 1200]])}
            aria-label="Stealth robot, tap to say hi" title="Say hi" data-testid="chat-robot-button">
            <StealthRobot size="100%" state={state} testid="chat-robot" />
          </button>
        </div>
        <Bubble state={state} />
      </div>
    </div>
  );
}
